import { jwtDecode } from "jwt-decode";
import { Item } from "../types/index";
import { ERROR_MESSAGES } from "./messageConstants";
import { IDENTITY_PROVIDER } from "./constants";

const SSO_USERNAME_PREFIXES = Object.values(IDENTITY_PROVIDER).map(
  (provider) => `${provider}_`
);

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

export const decodeToken = (token: any): any => {
  const decodedToken = jwtDecode(token);
  return decodedToken;
};

/**
 * Fail-closed identity from Cognito access token: JWT `sub` only.
 * Never falls back to username / cognito:username / other claims.
 */
export const getUserIdFromAccessToken = (accessToken: string): string => {
  if (typeof accessToken !== "string" || accessToken.trim() === "") {
    throw new Error(ERROR_MESSAGES.ACCESS_TOKEN_SUB_MISSING);
  }
  let decoded: { sub?: unknown };
  try {
    decoded = decodeToken(accessToken) as { sub?: unknown };
  } catch {
    throw new Error(ERROR_MESSAGES.ACCESS_TOKEN_SUB_MISSING);
  }
  const sub = decoded?.sub;
  if (typeof sub !== "string" || sub.trim() === "") {
    throw new Error(ERROR_MESSAGES.ACCESS_TOKEN_SUB_MISSING);
  }
  return sub;
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

export const extractItemsFromPayload = (payload: any): Item[] => {
  if (!payload) return [];

  if (payload.data !== undefined) {
    const data = payload.data;
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.items && Array.isArray(data.items)) return data.items;
  }
  if (Array.isArray(payload)) return payload;
  if (payload.items && Array.isArray(payload.items)) return payload.items;
  return [];
};

export const checkIsAdmin = (idToken: string | null): boolean => {
  if (!idToken) return false;
  try {
    const decoded = decodeToken(idToken) as any;
    const groups = decoded["cognito:groups"] as string[] | undefined;
    return Array.isArray(groups) && groups.includes("ADMIN");
  } catch (error) {
    return false;
  }
};

export const checkIsSuperAdmin = (idToken: string | null): boolean => {
  if (!idToken) return false;
  try {
    const decoded = decodeToken(idToken) as any;
    const groups = decoded["cognito:groups"] as string[] | undefined;
    return Array.isArray(groups) && groups.includes("SUPER_ADMIN");
  } catch (error) {
    return false;
  }
};

/**
 * Cognito Hosted UI federated users have usernames like Google_… / Facebook_… / SignInWithApple_….
 */
export const isSsoUser = (idToken: string | null): boolean => {
  if (!idToken) return false;
  try {
    const decoded = decodeToken(idToken) as {
      "cognito:username"?: unknown;
      username?: unknown;
    };
    const username =
      typeof decoded["cognito:username"] === "string"
        ? decoded["cognito:username"]
        : typeof decoded.username === "string"
          ? decoded.username
          : "";
    return SSO_USERNAME_PREFIXES.some((prefix) => username.startsWith(prefix));
  } catch {
    return false;
  }
};

export const ITEM_SHARE = {
  TITLE: "Zelpye goodie",
  BLURB:
    "A goodie worth a look. Browse more on Zelpye.",
} as const;

export function buildItemShareMessage(itemUrl: string): string {
  return [
    "Take a look at this goodie on Zelpye.",
    "",
    itemUrl,
    "",
    "Discover more goodies at Zelpye.",
  ].join("\n");
}

export function buildFacebookSharerUrl(shareUrl: string): string {
  const url = encodeURIComponent(shareUrl);
  const quote = encodeURIComponent(buildItemShareMessage(shareUrl));
  return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`;
}
